import * as React from "react"
import { AlertTriangle } from "lucide-react"
import { Button } from "@vaiu/ui/button"
import { cn } from "@vaiu/utils"

export interface GuestLogoutConfirmBarProps {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
  title: string
  description: string
  cancelLabel: string
  confirmLabel: string
  className?: string
}

/**
 * Inline confirmation shown at the top of the app when a guest initiates sign-out.
 * Avoids modal stacking / z-index issues with dropdown menus.
 */
export function GuestLogoutConfirmBar({
  open,
  onCancel,
  onConfirm,
  title,
  description,
  cancelLabel,
  confirmLabel,
  className,
}: GuestLogoutConfirmBarProps) {
  if (!open) return null

  return (
    <div
      role="region"
      aria-label={title}
      aria-live="polite"
      className={cn(
        "sticky top-0 z-50 w-full border-b border-amber-200/80 bg-amber-50 text-foreground shadow-sm",
        "dark:border-amber-800/80 dark:bg-amber-950/50",
        className
      )}
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-1 gap-3">
          <AlertTriangle
            className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-semibold">{title}</p>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:pt-0.5">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button type="button" variant="destructive" size="sm" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
